import { useModal } from "@/Contexts";
import { accessManagementColumns } from "@/Utils/tableColumns/accessManagementColumns";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { Table } from "@/components/Table";
import { Button } from "@/components/button";
import { Card, CardComponentVariant } from "@/components/pageCards/card";
import { useTrans } from "@/translations/functions/hook";

import { FC, useMemo } from "react";

import { CustomerWithDetailsAndCode } from "server/coreApi/customers/types.js";
import { NormalizedCustomerIdentities } from "server/coreApi/identities/types.js";

import { AccessTokensModal } from "./accessTokens/AccessTokensModal";

interface AccessManagementCardProps {
  customer: CustomerWithDetailsAndCode;
  refetch: () => Promise<any>;
}

const ACCESS_TOKENS_LIMIT = 50;

export const AccessManagementCard: FC<AccessManagementCardProps> = ({
  customer,
}) => {
  const { showModal } = useModal();
  const { config } = useConfig();
  const { t } = useTrans();

  const { data: identitiesPages, refetch } = trpc.identities.get.useQuery(
    {
      customerIds: [customer.id],
      limit: ACCESS_TOKENS_LIMIT + 1,
    },
    {
      gcTime: 0,
    },
  );

  const identities = useMemo(
    () => (identitiesPages?.items || []) as NormalizedCustomerIdentities,
    [identitiesPages],
  );

  const identitiesWithTranslatedStatus = identities
    .slice(0, ACCESS_TOKENS_LIMIT)
    .map((el) => ({
      ...el,
      status: t(`status:${el.status}`),
    }));

  const showPreview = useMemo(
    () => identities.length > ACCESS_TOKENS_LIMIT,
    [identities],
  );

  const manageAccess = () => {
    showModal(
      <AccessTokensModal
        customerId={customer.id}
        customerName={customer.name}
        refetch={refetch}
      />,
    );
  };

  return (
    <Card
      {...{
        title: "drawerLabels:accessManagement",
      }}
      overflow="auto"
      variant={CardComponentVariant.DRAWER}
    >
      <p className="font-dm-sans mb-2 text-sm">{t("hints:accessTokenCard")}</p>
      <Table
        limitedHeight
        columns={accessManagementColumns}
        data={identitiesWithTranslatedStatus}
        emptyViewType="noAccessTokens"
        emptyViewClassName="bg-grey1 mt-0"
        showPreview={showPreview}
        previewHintKey="hints:previewAccessTokens"
      />
      {(!config.useExternalCRM || !customer.crmId) && (
        <div className="h-20 py-4">
          <Button
            className="float-right ml-8"
            label="actions:edit"
            color="secondary"
            onClick={manageAccess}
          />
        </div>
      )}
    </Card>
  );
};
